import { formatDate } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ContentFull, ContentHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { CrmTable, CrmTableProps } from "components/table/crm-table";
import { DeleteButton } from "components/ui/action-button";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { useMessage } from "hooks/use-message";
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useFeature } from "store/selectors/hooks";

interface WebhookEndpoint {
    id: string;
    url: string;
    dateAdded: string;
    signingSecret: string;
}

export const ManageWebhooks: React.FC<unknown> = () => {
    const [url, setUrl] = useState("");
    const { message, setMessage } = useMessage();
    const trpcClient = trpc.useTRPC();

    const isFeatureEnabled = useFeature("webhooks");

    const webhooksQuery = useQuery(trpcClient.webhooks.list.queryOptions());

    const createWebhookMutation = useMutation({
        ...trpcClient.webhooks.create.mutationOptions(),
        onSuccess: () => {
            setMessage("success", "Webhook created successfully");
            setUrl("");
            webhooksQuery.refetch();
        },
        onError: error => {
            setMessage("error", error.message || "Failed to create webhook");
        },
    });

    const deleteWebhookMutation = useMutation({
        ...trpcClient.webhooks.delete.mutationOptions(),
        onSuccess: () => {
            setMessage("success", "Webhook deleted successfully");
            webhooksQuery.refetch();
        },
        onError: error => {
            setMessage("error", error.message || "Failed to delete webhook");
        },
    });

    const handleCreateWebhook = () => {
        if (!url.trim()) {
            setMessage("error", "Please enter a webhook URL");
            return;
        }
        createWebhookMutation.mutate({ url });
    };

    const handleDeleteWebhook = useCallback(
        (id: string) => {
            deleteWebhookMutation.mutate({ id });
        },
        [deleteWebhookMutation],
    );

    const columns: CrmTableProps<WebhookEndpoint>["columns"] = useMemo(
        () => [
            {
                key: "url",
                header: "URL",
                renderCell: ({ record }) => (
                    <Link
                        to={`/settings/integrations/webhooks/${record.id}`}
                        className={defaultLinkStyle}>
                        {record.url}
                    </Link>
                ),
            },
            {
                key: "dateAdded",
                header: "Created On",
                renderCell: ({ record }) => <>{formatDate(new Date(record.dateAdded))}</>,
            },
            {
                key: "_delete_" as keyof WebhookEndpoint,
                header: "",
                renderCell: ({ record }) => (
                    <DeleteButton
                        backgroundColor="bg-white"
                        confirm={{
                            title: "Delete webhook?",
                            message: "Really delete this webhook endpoint?",
                            confirmLabel: "Yes, delete",
                        }}
                        onClick={() => handleDeleteWebhook(record.id)}
                    />
                ),
            },
        ],
        [handleDeleteWebhook],
    );

    const webhooks = webhooksQuery.data || [];

    if (!isFeatureEnabled) {
        return null;
    }

    return (
        <ContentFull>
            <ContentHeader>Webhooks</ContentHeader>
            <div className="space-y-6">
                <p>
                    Webhooks allow you to receive real-time notifications when events
                    occur in your account.
                </p>

                {webhooks.length === 0 ? (
                    <InlineBanner type="info">
                        No webhooks have been created. Use the &quot;Add Webhook&quot;
                        form below to create a new webhook.
                    </InlineBanner>
                ) : (
                    <CrmTable columns={columns} data={webhooks} />
                )}

                <div className="pt-6">
                    {message && (
                        <div className="mb-4">
                            <InlineBanner type={message.type}>
                                {message.content}
                            </InlineBanner>
                        </div>
                    )}
                    <div className="flex gap-3 items-end">
                        <div className="flex-1">
                            <Input
                                label="Add Webhook"
                                value={url}
                                onChange={e => setUrl(e.target.value)}
                                placeholder="https://example.com/webhook"
                                required
                            />
                        </div>
                        <PrimaryButton
                            onClick={handleCreateWebhook}
                            disabled={createWebhookMutation.isPending}>
                            {createWebhookMutation.isPending
                                ? "Creating..."
                                : "Create Webhook"}
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </ContentFull>
    );
};
